import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import i18n from "../Translations/i18n/i18n";
import { useTranslation } from 'react-i18next';
import logoIcon from '../../assets/images/logo.png';
import logoutIcon from '../../assets/images/logout.png';
import '../../App.css';
import { Amplify, Auth } from 'aws-amplify';
import Widget from './Widget';

const Header = React.memo(() => {
  const location = useLocation();
  const [title, setTitle] = useState('');
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 95);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loggedIn = async () => {
    const session = await Amplify.Auth.currentSession();
    if (!session) {
			console.log("Unable to find current session!");
			return false;
		}

		localStorage.setItem("accounts", session["idToken"]["payload"]["cognito:groups"]);
    return true;
  }

  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();
    const pathToTitleMap = {
      '/login': t('Login'),
      '/auth': 'SSO Authentication',
      '/search': t('Restaurants'),
      '/restaurants': t('Restaurants'),
      '/announcements': t('Announcements'),
      '/settings': t('Settings'),
      '/dashboard': t('Dashboard'),
      '/incident': t('Incident Details'),
      '/new_incident': t('New Incident'),
      "/new_rfm": t('New RFM Package'),
      "/new_cap": t('New CAP Request'),
      '/knowledge': t('Knowledge'),
      "/knowledge/articles": t('Available Articles'),
      "/knowledge/article": t('Knowledge Article'),
    };

    let newTitle = pathToTitleMap[currentPath] || t('My Map');

    if (!loggedIn) {
      newTitle = t('Login');
    }

    setTitle(newTitle);
    document.title = newTitle;
  }, [location.pathname, loggedIn, t]);

  const handleLanguageChange = (event) => {
    const selectedLanguage = event.target.value;

    localStorage.setItem("language", selectedLanguage);
    i18n.changeLanguage(selectedLanguage);

    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const getRestaurantTabLink = () => {
    return localStorage.getItem("user_power_user") === "true" ? "/search" : "/restaurants";
  };

  const signOut = (event) => {
    event.stopPropagation();
    localStorage.setItem("user_identifier", "");
    localStorage.setItem("user_power_user", false);
    Auth.signOut().then(() => {
      window.location.href = "login";
    })
  };

  const bookmarkStyle = {
    position: 'fixed',
    top: showScrollTop ? '0' : '-60px',
    right: '24px',
    zIndex: 9999,
    background: 'linear-gradient(180deg, #DA291C 0%, #C20D00 100%)',
    border: 'none',
    borderRadius: '0 0 8px 8px',
    width: '44px',
    height: '52px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingBottom: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    transition: 'top 0.3s ease, box-shadow 0.2s ease',
  };

  const bookmarkHoverStyle = {
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)',
  };

  const arrowStyle = {
    color: 'white',
    fontSize: '20px',
    fontWeight: 'bold',
    lineHeight: 1,
  };

  const widgetWrapperStyle = {
    position: 'relative',
    opacity: showScrollTop ? 0 : 1,
    pointerEvents: showScrollTop ? 'none' : 'auto',
    transition: 'opacity 0.3s ease',
  };

  return (
    <header>
      <div className='header-container'>
        <a href="/" className='Logo'>
          <img className='App-logo' src={logoIcon} alt="McDonald's" />
        </a>
        <h1 className='header-title'>{title}</h1>
        
        <div id='header-info'>
          <select name='language' value={i18n.language} onChange={handleLanguageChange}>
            <option value="fq">{t('french')}</option>
            <option value="en">{t('english')}</option>
          </select>
        </div>
        {localStorage.getItem("user_identifier") != "" ? (
          <>
            <div className="widget-wrapper" style={widgetWrapperStyle}>
              <Widget buttonId="launch-widget-btn-desktop" isInMenu={false} />
            </div>
            <button className='logout-button' onClick={signOut}>
              <img src={logoutIcon} alt="Log Out" />
            </button>
          </>
        ) : (
          <></>
        )}
          
      </div>

      <nav className="Navbar">
        <div className="menu-icon">
          <span onClick={toggleMenu}>☰</span>
          <p className="navbar-title">{title}</p>
                    
          {localStorage.getItem("user_identifier") != "" ? (
            <>
              <div className="widget-wrapper" style={{ position: 'relative' }}>
                <Widget buttonId="launch-widget-btn-mobile" isInMenu={true} />
              </div>
              <button className='logout-button' onClick={signOut}>
                <img src={logoutIcon} alt="Log Out" />
              </button>
            </>
          ) : (
            <></>
          )}
        </div>
        <ul className={showMenu ? "menu show" : "menu hidden"}>
          <li>
            <Link to="/home">{t('myMap')}</Link>
          </li>
          <li>
            <Link to={getRestaurantTabLink()}>{t('restaurants')}</Link>
          </li>
          <li>
            <Link to="/knowledge">{t('knowledge')}</Link>
          </li>
          <li>
            <Link to="/settings">{t('settings')}</Link>
          </li>
          <li>
            <Link to="/announcements">{t('announcements')}</Link>
          </li>
        </ul>
      </nav>

      <button onClick={scrollToTop} style={bookmarkStyle}
        onMouseEnter={(e) => Object.assign(e.target.style, bookmarkHoverStyle)}
        onMouseLeave={(e) => e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)'}
      >
        <span style={arrowStyle}>↑</span>
      </button>
    </header>
  );
});

export default Header;